import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  FC,
  ReactNode,
} from 'react';
import { OrganizationService } from '../organizationService';
import {
  groupOrganizationUnits,
  errorToast,
  getIdAttributes,
} from '../../utils/helper';
import {
  OrganizationUnit,
  OrganizationUnitType,
  VenueBranch,
} from '@inplay/models';
import {
  GroupedOrganizationUnit,
  OrganizationContextType,
} from './organizationContext.types';
import { useAuth } from './authContext';

export const idPrefix: string = 'organization-context';

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const OrganizationProvider: FC<{
  children: ReactNode;
  testIdPrefix?: string;
}> = ({ children, testIdPrefix }) => {
  const prefix: string = testIdPrefix || idPrefix;
  const [organizationSummary, setOrganizationSummary] =
    useState<(OrganizationUnit & { _id: string })[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [groupedOrganizationData, setGroupedOrganizationData] = useState<
    GroupedOrganizationUnit | undefined
  >();
  const [selectedVenueBranch, setSelectedVenueBranch] = useState<
    VenueBranch & { _id: string }
  >();
  const { accessToken } = useAuth();

  useEffect(() => {
    if (!accessToken) {
      setOrganizationSummary(undefined);
      setGroupedOrganizationData(undefined);
      setSelectedVenueBranch(undefined);
    }
  }, [accessToken]);

  const fetchOrganizationSummary = async () => {
    setLoading(true);
    try {
      const service: OrganizationService = new OrganizationService();
      const data: (OrganizationUnit & { _id: string })[] =
        await service.getOrganizationUnits();
      setSelectedVenueBranch(
        data.find(
          (item) => item.type === OrganizationUnitType.VENUE_BRANCH,
        ) as VenueBranch & { _id: string },
      );
      setOrganizationSummary(data);
    } catch (error: unknown) {
      errorToast(error);
      console.error('[OrganizationContext] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchOrganizationSummary();
  }, [accessToken]);

  useEffect(() => {
    if (!loading && organizationSummary) {
      const groupedData: GroupedOrganizationUnit | undefined =
        groupOrganizationUnits(organizationSummary);
      setGroupedOrganizationData(groupedData);
      console.log(`[OrganizationContext] Grouped data:`, groupedData);
    }
  }, [loading, organizationSummary]);

  const updateSelectedVenueBranch = (branch: VenueBranch & { _id: string }) => {
    setSelectedVenueBranch(branch);
  };

  const contextValue = useMemo(
    () => ({
      organizationSummary,
      loading,
      groupedOrganizationData,
      selectedVenueBranch,
      updateSelectedVenueBranch,
      fetchOrganizationSummary,
    }),
    [
      organizationSummary,
      loading,
      groupedOrganizationData,
      selectedVenueBranch,
    ],
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      <div {...getIdAttributes(prefix, 'provider')}>{children}</div>
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context)
    throw new Error(
      'useOrganizationContext must be used within OrganizationProvider',
    );
  return context;
};
